/**
 * IncassoKanbanView
 *
 * Wraps the shared Kanban component with incasso-specific configuration.
 * Handles optimistic phase updates via drag-drop and delegates to the
 * shared hook for persistence.
 *
 * Follows the exact same pattern as HelpdeskKanbanView.
 */

import { useState, useEffect, memo, useCallback } from 'react';
import { Kanban } from 'shared/components/Kanban';
import { incassoKanbanConfig } from 'shared/components/Kanban/configs/incassoKanbanConfig';
import { toast } from 'react-toastify';
import { IncassoTicket, IncassoPhase, IncassoBeoordeling } from '../shared/incassoTypes';
import { IncassoCard } from './IncassoCard';
import {
  getPhaseLabel,
  isBackwardMove,
} from '../configs/incassoPhaseConfig';

// ─── Phases that trigger a phase transition dialog ────────────────────────────
const DIALOG_PHASES = new Set<IncassoPhase>([
  IncassoPhase.MINNELIJKE_REGELING,
  IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN,
]);

// ─── Props ────────────────────────────────────────────────────────────────────

interface IncassoKanbanViewProps {
  tickets: IncassoTicket[];
  onPhaseChange: (ticketId: string, newPhase: IncassoPhase) => Promise<void>;
  onMoveTicketLocally: (
    ticketId: string,
    fromPhase: IncassoPhase,
    toPhase: IncassoPhase,
  ) => void;
  onTicketClick: (ticket: IncassoTicket) => void;
  onBeoordelingChange?: (ticket: IncassoTicket, beoordeling: IncassoBeoordeling | null, reden?: string) => void;
  /** Triggered when drag hits a phase that requires a dialog (before API call) */
  onPhasePending?: (payload: {
    ticket: IncassoTicket;
    fromPhase: IncassoPhase;
    toPhase: IncassoPhase;
  }) => void;
  columnPagination?: Array<{
    columnId: IncassoPhase;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    loadMoreText?: string;
    loadingText?: string;
  }>;
  hiddenColumns?: string[];
  onToggleColumnVisibility?: (columnId: string) => void;
  fontSize?: number;
  onIncreaseFontSize?: () => void;
  onDecreaseFontSize?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const IncassoKanbanViewComponent = ({
  tickets,
  onPhaseChange,
  onMoveTicketLocally,
  onTicketClick,
  onBeoordelingChange,
  onPhasePending,
  columnPagination,
  hiddenColumns,
  onToggleColumnVisibility,
  fontSize = 12,
  onIncreaseFontSize,
  onDecreaseFontSize,
}: IncassoKanbanViewProps) => {
  // Local state for optimistic updates (mirrors helpdesk pattern)
  const [localTickets, setLocalTickets] = useState<IncassoTicket[]>(
    tickets.filter((t) => t.id !== undefined),
  );

  // Sync when parent data changes (after full refresh)
  useEffect(() => {
    const filtered = tickets.filter((t) => t.id !== undefined);
    const currentIds = localTickets.map((t) => `${t.id}-${t.phase}`).sort().join(',');
    const newIds = filtered.map((t) => `${t.id}-${t.phase}`).sort().join(',');
    if (currentIds !== newIds) {
      setLocalTickets(filtered);
    }
  }, [tickets, localTickets]);

  /**
   * Handle a ticket being dropped onto a new phase column.
   * Triggers phase-transition dialog for specific phases.
   * Otherwise performs optimistic update + API call.
   */
  const handleDrop = useCallback(
    async (ticketId: string | number, newPhase: IncassoPhase, oldPhase: IncassoPhase) => {
      const id = ticketId;
      const ticket = localTickets.find((t) => String(t.id) === String(id));
      if (!ticket) return;

      // Check if this move needs a dialog (specific phases or backward move)
      const needsDialog =
        DIALOG_PHASES.has(newPhase) || isBackwardMove(oldPhase, newPhase);

      if (needsDialog && onPhasePending) {
        onPhasePending({ ticket, fromPhase: oldPhase, toPhase: newPhase });
        return; // Dialog will call handlePhaseCommit when confirmed
      }

      const strId = String(id);

      // OPTIMISTIC UPDATE: move ticket visually right away
      setLocalTickets((prev) =>
        prev.map((t) => (String(t.id) === strId ? { ...t, phase: newPhase } : t)),
      );
      onMoveTicketLocally(strId, oldPhase, newPhase);
      toast.success(`Dossier verplaatst naar "${getPhaseLabel(newPhase)}"`);

      // Persist to backend
      try {
        await onPhaseChange(strId, newPhase);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Onbekende fout';
        console.error('[IncassoKanbanView] Phase change failed:', error);
        toast.error(`Fout bij verplaatsen: ${message}`);

        // ROLLBACK
        setLocalTickets((prev) =>
          prev.map((t) => (String(t.id) === strId ? { ...t, phase: oldPhase } : t)),
        );
        onMoveTicketLocally(strId, newPhase, oldPhase);
      }
    },
    [localTickets, onPhaseChange, onMoveTicketLocally, onPhasePending],
  );

  /**
   * Render a single incasso card inside the kanban column.
   */
  const renderCard = useCallback(
    (ticket: IncassoTicket) => (
      <IncassoCard
        ticket={ticket}
        onClick={() => onTicketClick(ticket)}
        onBeoordelingChange={(beoordeling, reden) => onBeoordelingChange?.(ticket, beoordeling, reden)}
        fontSize={fontSize}
      />
    ),
    [onTicketClick, onBeoordelingChange, fontSize],
  );

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-0">
        <Kanban
          config={incassoKanbanConfig as any}
          items={localTickets as any}
          renderCard={renderCard as any}
          onDrop={handleDrop as any}
          onCardClick={onTicketClick as any}
          columnPagination={columnPagination as any}
          hiddenColumns={hiddenColumns as any}
          onToggleColumnVisibility={onToggleColumnVisibility as any}
          fontSize={fontSize}
          onIncreaseFontSize={onIncreaseFontSize}
          onDecreaseFontSize={onDecreaseFontSize}
        />
      </div>
    </div>
  );
};

export const IncassoKanbanView = memo(IncassoKanbanViewComponent);
